import React, { useContext, useEffect, useMemo } from 'react';
import { RicosContext } from 'ricos-context';
import type { IToolbarItemConfigTiptap, ToolbarButtonRenderers } from 'ricos-types';

import { ToolbarButtons } from '../../models';
import type { RicosToolbar } from '../../RicosToolbar';
import type { OverflownItemsPosition, ToolbarSpec } from '../../types';
import { filterExcessiveToolbarSeparators } from './FilterExcessiveToolbarSeparators';
import { RicosDesktopToolbar } from './RicosDesktopToolbar/RicosDesktopToolbar';
import { RicosMobileToolbar } from './RicosMobileToolbar/RicosMobileToolbar';
import { transformItemToButton } from './TransformItemToButton';
import { useCreateRicosToolbar } from './UseCreateRicosToolbar';

type ToolbarProps = {
  toolbarItemsRenderers: ToolbarButtonRenderers;
  toolbarItemsConfig: IToolbarItemConfigTiptap[];
  onLoad?: (toolbar: RicosToolbar) => void;
  alignment?: string;
  type: 'floating' | 'static' | 'regular';
  overflownItemsPosition?: OverflownItemsPosition;
  isCircularFocusEnabled?: boolean;
  onlyShownButtons?: string[];
  isMobile: boolean;
  maxWidth: number;
};

const visibleOnlySpec: ToolbarSpec = attributes => attributes.visible === true;

export const Toolbar = ({
  toolbarItemsConfig,
  onLoad,
  toolbarItemsRenderers,
  isCircularFocusEnabled = true,
  overflownItemsPosition,
  type,
  alignment,
  onlyShownButtons,
  isMobile,
  maxWidth,
}: ToolbarProps) => {
  const filteredItemsConfig = useMemo(
    () => filterExcessiveToolbarSeparators(toolbarItemsConfig),
    [toolbarItemsConfig]
  );
  const toolbar = useCreateRicosToolbar(filteredItemsConfig);
  const { languageDir } = useContext(RicosContext);
  const isRTL = languageDir === 'rtl';

  const visibleToolbarButtons = toolbar.getToolbarItemsBy(visibleOnlySpec);

  const toolbarButtons = useMemo(() => {
    return new ToolbarButtons(
      visibleToolbarButtons.map(toolbarItem =>
        transformItemToButton(toolbarItem, toolbarItemsRenderers)
      )
    );
  }, visibleToolbarButtons);

  useEffect(() => {
    onLoad?.(toolbar);
  }, [toolbar]);

  useEffect(() => {
    return () => {
      toolbar.destroyToolbarItems();
    };
  }, [toolbar]);

  if (isMobile) {
    return <RicosMobileToolbar maxWidth={maxWidth} toolbarButtons={toolbarButtons} type={type} />;
  }

  return (
    <RicosDesktopToolbar
      type={type}
      maxWidth={maxWidth}
      toolbarButtons={toolbarButtons}
      isRTL={isRTL}
      onlyShownButtons={onlyShownButtons}
      overflownItemsPosition={overflownItemsPosition}
      alignment={alignment}
      isCircularFocusEnabled={isCircularFocusEnabled}
    />
  );
};
